/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import BuildIcon from '@mui/icons-material/Build';
import DescriptionIcon from '@mui/icons-material/Description';
import InfoIcon from '@mui/icons-material/Info';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import SaveIcon from '@mui/icons-material/Save';
import {TabContext, TabList, TabPanel} from '@mui/lab';
import {Box, Button, Stack, Tab} from '@mui/material';
import {useFlags} from 'launchdarkly-react-client-sdk';
import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {v4 as uuidv4} from 'uuid';
import {AgentAbilities} from './components/AgentAbilities';
import {AgentAbout} from './components/AgentAbout';
import {AgentDocuments} from './components/AgentDocuments';
import {AgentFAQs} from './components/AgentFAQs';
import {useGetAgentAbilityTypes} from './hooks/useGetAgentAbilityTypes';
import {useGetAgentActionTypes} from './hooks/useGetAgentActionTypes';
import {useGetVoices} from './hooks/useGetVoices';
import {useUpdateAgent} from './hooks/useUpdateAgent';
import {PageContainer} from '../../components/PageContainer';
import {FeatureFlags, type FeatureFlagSet} from '../../flags';
import {useToast} from '../../hooks/useToast';
import {ROUTES} from '../../routes';
import type {AgentAbility, AgentFAQ, NewAgent} from '../../types/agent';

type AgentAbout = {
  name: string;
  greeting: string;
  persona: string;
  voiceId: string | undefined;
};

const AgentsCreatePage = () => {
  const {update, isLoading} = useUpdateAgent();
  const {toastSuccess, toastError} = useToast();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState('about');
  const [touched, setTouched] = useState({name: false, greeting: false, persona: false, voiceId: false});
  const [agentAbout, setAgentAbout] = useState<AgentAbout>({
    name: '',
    greeting: 'Hey, how can I help you today?',
    persona: '',
    voiceId: undefined,
  });
  const [agentFAQS, setAgentFAQS] = useState<AgentFAQ[]>([]);
  const [agentAbilities, setAgentAbilities] = useState<AgentAbility[]>([]);
  const {get: getAgentAbilityTypes, abilityTypes} = useGetAgentAbilityTypes();
  const {get: getAgentActionTypes, actionTypes} = useGetAgentActionTypes();
  const {getVoices, voices, isLoadingVoices} = useGetVoices();
  const flags = useFlags<FeatureFlagSet>();

  useEffect(() => {
    void getAgentAbilityTypes();
    void getAgentActionTypes();
    void getVoices();
  }, []);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const handleAgentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target;
    setAgentAbout({...agentAbout, [name]: value});
    setTouched({...touched, [name]: true});
  };

  const handleVoiceChange = (voiceId: string | undefined) => {
    setAgentAbout({...agentAbout, voiceId});
  };

  const isNameInvalid = touched.name && !agentAbout.name.trim();
  const isPersonaInvalid = touched.persona && !agentAbout.persona.trim();
  const isFormInvalid = !agentAbout.name.trim() || !agentAbout.persona.trim();

  const handleSave = async () => {
    try {
      const voiceProvider = voices?.find((v) => v.voiceId === agentAbout.voiceId)?.voiceProvider;
      const agent: NewAgent = {
        abilities: agentAbilities,
        faqs: agentFAQS,
        isDefault: false,
        name: agentAbout.name,
        greeting: agentAbout.greeting,
        persona: agentAbout.persona,
        voiceId: agentAbout.voiceId,
        voiceProvider: voiceProvider,
      };
      try {
        const createdAgent = await update(agent);
        navigate(ROUTES.AGENTS_EDIT(createdAgent.agentId));
        toastSuccess('Agent saved successfully.');
      } catch (e) {
        toastError('Failed to save agent. Please try again.');
      }
    } catch (e) {}
  };

  const handleCancel = () => {
    navigate(ROUTES.AGENTS_LIST);
  };

  return (
    <PageContainer
      title={'New Agent'}
      actions={
        <Stack direction={'row'} spacing={2}>
          <Button variant={'outlined'} color={'secondary'} onClick={handleCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant={'contained'}
            color={'primary'}
            onClick={handleSave}
            disabled={isLoading || isFormInvalid}
            startIcon={<SaveIcon />}
          >
            {isLoading ? 'Saving...' : 'Save'}
          </Button>
        </Stack>
      }
      compact
      stack={false}
    >
      <TabContext value={tabValue}>
        <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
          <TabList onChange={handleTabChange}>
            <Tab label={'About'} value={'about'} icon={<InfoIcon />} iconPosition={'start'} />
            <Tab label={'Abilities'} value={'abilities'} icon={<BuildIcon />} iconPosition={'start'} />
            {flags[FeatureFlags.featureAgentFaq] && (
              <Tab label={'FAQs'} value={'faqs'} icon={<QuestionAnswerIcon />} iconPosition={'start'} />
            )}
            <Tab label={'Documents'} value={'documents'} icon={<DescriptionIcon />} iconPosition={'start'} />
          </TabList>
        </Box>

        <TabPanel value={'about'}>
          <AgentAbout
            formValues={agentAbout}
            handleVoiceChange={handleVoiceChange}
            voices={voices}
            handleAgentChange={handleAgentChange}
            isNameInvalid={isNameInvalid}
            isPersonaInvalid={isPersonaInvalid}
            isLoadingVoices={isLoadingVoices}
          />
        </TabPanel>

        <TabPanel value={'abilities'}>
          <AgentAbilities
            abilities={agentAbilities}
            setAbilities={setAgentAbilities}
            abilityTypes={abilityTypes}
            actionTypes={actionTypes}
          />
        </TabPanel>

        {flags[FeatureFlags.featureAgentFaq] && (
          <TabPanel value={'faqs'}>
            <AgentFAQs
              faqs={agentFAQS.map((faq) => ({
                ...faq,
                questionId: faq.questionId || uuidv4(),
              }))}
              setFaqs={setAgentFAQS}
            />
          </TabPanel>
        )}

        <TabPanel value={'documents'}>
          <AgentDocuments isSaved={false} />
        </TabPanel>
      </TabContext>
    </PageContainer>
  );
};

export default AgentsCreatePage;
